/**
 * Analytics tracking for Conversion Rate Optimization
 * Tracks user interactions with CTAs and conversion funnels
 */

export type AnalyticsEvent = 
  | 'cta_get_quote_click'
  | 'cta_call_now_click'
  | 'cta_chat_open'
  | 'cta_chat_submit'
  | 'service_link_click'
  | 'location_link_click'
  | 'gallery_image_view'
  | 'testimonial_view'
  | 'form_submit'
  | 'phone_call_click'
  | 'email_click'
  | 'page_scroll'
  | 'video_play';

export interface AnalyticsEventData {
  event: AnalyticsEvent;
  page: string;
  section?: string;
  cta_text?: string;
  cta_position?: 'header' | 'hero' | 'content' | 'footer' | 'sidebar';
  value?: number;
  timestamp: number;
}

/**
 * Track analytics events for CRO
 */
export const trackEvent = (event: AnalyticsEvent, data?: Partial<AnalyticsEventData>) => {
  try {
    const eventData: AnalyticsEventData = {
      event,
      page: window.location.pathname,
      timestamp: Date.now(),
      ...data,
    };

    // Send to Plausible Analytics if available
    if (window.plausible) {
      window.plausible(event, { props: eventData });
    }

    // Also log to custom analytics endpoint for heat mapping
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/analytics/track', JSON.stringify(eventData));
    }

    console.debug('[Analytics]', event, eventData);
  } catch (error) {
    console.error('[Analytics Error]', error);
  }
};

/**
 * Track CTA clicks with position and text
 */
export const trackCTAClick = (
  ctaType: 'get_quote' | 'call_now' | 'chat',
  position: 'header' | 'hero' | 'content' | 'footer' | 'sidebar' = 'content',
  text?: string
) => {
  const eventMap = {
    get_quote: 'cta_get_quote_click' as const,
    call_now: 'cta_call_now_click' as const,
    chat: 'cta_chat_open' as const,
  };

  trackEvent(eventMap[ctaType], {
    cta_position: position,
    cta_text: text,
    section: document.querySelector('main')?.id || 'unknown',
  });
};

/**
 * Track scroll depth for engagement
 */
export const trackScrollDepth = () => {
  let maxScroll = 0;

  const handleScroll = () => {
    const scrollPercentage = 
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

    if (scrollPercentage > maxScroll) {
      maxScroll = scrollPercentage;

      // Track at 25%, 50%, 75%, 100% scroll
      if (maxScroll >= 25 && maxScroll < 50) {
        trackEvent('page_scroll', { value: 25 });
      } else if (maxScroll >= 50 && maxScroll < 75) {
        trackEvent('page_scroll', { value: 50 });
      } else if (maxScroll >= 75 && maxScroll < 100) {
        trackEvent('page_scroll', { value: 75 });
      } else if (maxScroll >= 100) {
        trackEvent('page_scroll', { value: 100 });
      }
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  return () => window.removeEventListener('scroll', handleScroll);
};

declare global {
  interface Window {
    plausible?: (event: string, options?: any) => void;
  }
}
